import type { Resource } from "./useResource";
import type { SeasonArchive, SeasonManagerAliases } from "@averagejoesff/shared";
import { useLeagueDocs } from "./LeagueDocsProvider";

/**
 * Resolves the manager label to show next to each team name for a season:
 * an admin-set override (`SeasonManagerAliases`) when one exists, else
 * Yahoo's own `Team.managerNames` joined together. The aliases come from
 * `LeagueDocsProvider`, so a save from the Edit Season panel updates every
 * label on the page at once; `setAliases` is that shared setter.
 */
export function useManagerNames(
  archive: SeasonArchive,
): {
  labelByTeam: Map<string, string>;
  aliases: Resource<SeasonManagerAliases | null>;
  setAliases: (aliases: SeasonManagerAliases) => void;
} {
  const { managerAliases: aliases, setManagerAliases: setAliases } = useLeagueDocs();

  const current = aliases.status === "ready" ? aliases.data : null;
  const overrideByTeam = new Map(
    current?.teams.filter((t) => t.managerName.trim() !== "").map((t) => [t.name, t.managerName]) ?? [],
  );

  const labelByTeam = new Map(
    archive.teams.map((t) => [t.name, overrideByTeam.get(t.name) ?? t.managerNames.join(" & ")]),
  );

  return { labelByTeam, aliases, setAliases };
}
